// Dual-timezone date formatting.
//
// Project convention: every timestamp shown in the UI renders Bangladesh time
// (Asia/Dhaka) alongside PST (America/Los_Angeles), and premium expiry is reasoned
// about in BD time. Route all date displays through these helpers so the two zones
// stay consistent everywhere.

import { TIMEZONES } from "./Constants";
import { isEmpty } from "./BasicUtilities";

// Normalize anything date-ish (ISO string, ms number, Date) to a valid Date or null.
function toDate(value) {
    if (isEmpty(value)) return null;
    const d = value instanceof Date ? value : new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

// Render an instant in a single IANA zone, e.g. "30 Jun 2026, 8:00 PM".
function formatInZone(date, timeZone, { withTime = true } = {}) {
    const options = {
        timeZone,
        day: "2-digit",
        month: "short",
        year: "numeric",
    };
    if (withTime) {
        options.hour = "2-digit";
        options.minute = "2-digit";
        options.hour12 = true;
    }
    return new Intl.DateTimeFormat("en-GB", options).format(date);
}

// Bangladesh-only rendering, e.g. "30 Jun 2026, 8:00 PM BD".
export function formatBd(value, { withTime = true } = {}) {
    const date = toDate(value);
    if (!date) return "—";
    return `${formatInZone(date, TIMEZONES.BD, { withTime })} BD`;
}

// PST-only rendering, e.g. "30 Jun 2026, 7:00 AM PST".
export function formatPst(value, { withTime = true } = {}) {
    const date = toDate(value);
    if (!date) return "—";
    return `${formatInZone(date, TIMEZONES.PST, { withTime })} PST`;
}

// Both zones in one string, e.g.
//   "30 Jun 2026, 8:00 PM BD · 30 Jun 2026, 7:00 AM PST".
// `withTime: false` drops the clock for date-only contexts.
export function formatBdPst(value, { withTime = true } = {}) {
    const date = toDate(value);
    if (!date) return "—";
    return `${formatBd(date, { withTime })} · ${formatPst(date, { withTime })}`;
}

// Offset (ms) between the given IANA zone and UTC at a specific instant — DST-aware.
// Positive means the zone is ahead of UTC. Uses Intl so no tz database is bundled.
function zoneOffsetMs(timeZone, date) {
    const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
    const parts = dtf.formatToParts(date).reduce((acc, p) => {
        if (p.type !== "literal") acc[p.type] = Number(p.value);
        return acc;
    }, {});
    // Intl renders hour 24 for midnight in some engines; normalize to 0.
    const hour = parts.hour === 24 ? 0 : parts.hour;
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, hour, parts.minute, parts.second);
    return asUtc - date.getTime();
}

// Interpret a naive "datetime-local" wall-clock string (e.g. "2026-08-10T14:00", no zone) as a
// time in the given IANA zone and return the corresponding UTC instant as an ISO string.
// So "2026-08-10T14:00" in PST => the ISO for 2 PM Los Angeles that day. Returns null if empty.
export function zonedInputToIso(localStr, timeZone) {
    if (isEmpty(localStr)) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(localStr);
    if (!m) return null;
    const [, y, mo, d, h, mi] = m.map(Number);
    // Treat the wall clock as if it were UTC, then subtract the zone's offset at that instant to
    // land on the true UTC instant. (Standard two-step; accurate outside the rare DST-gap hour.)
    const guessUtcMs = Date.UTC(y, mo - 1, d, h, mi);
    const offset = zoneOffsetMs(timeZone, new Date(guessUtcMs));
    return new Date(guessUtcMs - offset).toISOString();
}

// Format a UTC instant (ISO/ms/Date) as a naive "datetime-local" value ("YYYY-MM-DDTHH:mm")
// showing the wall-clock time in the given IANA zone — the inverse of zonedInputToIso, for
// prefilling <input type="datetime-local"> fields. Returns "" if empty.
export function isoToZonedInput(value, timeZone) {
    const date = toDate(value);
    if (!date) return "";
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    })
        .formatToParts(date)
        .reduce((acc, p) => {
            if (p.type !== "literal") acc[p.type] = p.value;
            return acc;
        }, {});
    const hour = parts.hour === "24" ? "00" : parts.hour;
    return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
}
